import {
  chipLabel,
  stripTrailingUnitSuffixes,
  type ChipSide,
  type FieldUnitPairId,
} from '../utils/fieldUnitConversion';

export const DIMENSION_UNIT_DEFAULTS_KEY = 'dimension_unit_defaults';

const FIELD_PAIRS: Record<string, FieldUnitPairId> = {
  siteCeilingHeight: 'lengthFt',
  siteDroppedCeilingHeight: 'lengthFt',
  sitePlenumHeight: 'lengthFt',
  siteSlabToDroppedCeilingHeight: 'lengthFt',
  siteRoomLengthFtIn: 'lengthFt',
  siteRoomWidthSqftSqm: 'lengthFt',
  powerBuswayLengthFt: 'lengthFt',
  siteRaisedFloorHeight: 'lengthIn',
  rackDimensionsLength: 'lengthIn',
  rackDimensionsWidth: 'lengthIn',
  siteWeightSupportOfFloor: 'floorWeight',
  coolingTempRisePrefThroughItEquipment: 'temperature',
};

/** The 12 field-level chips. Keep the audit test in lockstep with this list. */
export const DIMENSION_UNIT_FIELD_KEYS = Object.freeze(
  Object.keys(FIELD_PAIRS),
);

const UNIT_TO_SIDE: Record<string, ChipSide> = {
  ft: 'america',
  m: 'other',
  in: 'america',
  mm: 'other',
  'lb/sqft': 'america',
  'lbs/sqft': 'america',
  'kg/m2': 'other',
  F: 'america',
  C: 'other',
};

const PAIR_CODES: Record<FieldUnitPairId, ReadonlySet<string>> = {
  lengthFt: new Set(['ft', 'm']),
  lengthIn: new Set(['in', 'mm']),
  floorWeight: new Set(['lbs/sqft', 'kg/m2']),
  temperature: new Set(['F', 'C']),
};

export type DimensionUnits = Record<string, string>;

const INSTANCE_UNIT_KEY = /^([A-Za-z][A-Za-z0-9]*)#(-?\d+)$/;

export function dimensionUnitStorageKey(
  fieldKey: string,
  instanceId?: number,
): string {
  return instanceId == null ? fieldKey : `${fieldKey}#${instanceId}`;
}

/**
 * Wire field on a legacy family POST. The server moves `#tempId` onto the id
 * it just assigned. Aggregate save uses `clientKey` instead, so do not put
 * this on that payload. Must match `takeDimensionUnitTempId` on the server.
 */
export const DIMENSION_UNIT_TEMP_ID_FIELD = 'dimensionUnitTempId';

export function withDraftDimensionUnitTempId<T extends Record<string, unknown>>(
  draftId: number | undefined,
  payload: T,
): T {
  if (
    typeof draftId !== 'number' ||
    !Number.isSafeInteger(draftId) ||
    draftId >= 0
  ) {
    return payload;
  }
  return {
    ...payload,
    [DIMENSION_UNIT_TEMP_ID_FIELD]: draftId,
  } as T;
}

/** Keep a negative draft id. Replacing it strands the chip on the old key. */
export function stableDraftInstanceId(
  id: number | undefined,
  index: number,
): number {
  if (typeof id === 'number' && Number.isFinite(id) && id !== 0) return id;
  return -(Date.now() + index);
}

/**
 * Field-level codes for the next request. Instance keys (`rackDimensionsLength#4`)
 * fold onto the field so a saved rack chip is the default for new racks.
 */
export function fieldLevelDimensionUnits(
  units: DimensionUnits,
): DimensionUnits {
  const next: DimensionUnits = {};
  const fromInstance: DimensionUnits = {};
  for (const [key, code] of Object.entries(units)) {
    const match = INSTANCE_UNIT_KEY.exec(key);
    if (match) {
      fromInstance[match[1]] = code;
    } else {
      next[key] = code;
    }
  }
  return { ...next, ...fromInstance };
}

/** Fields whose chip belongs to one saved row, not the whole request. */
export const INSTANCE_UNIT_FIELDS = {
  racks: new Set(['rackDimensionsLength', 'rackDimensionsWidth']),
  coolingSystems: new Set(['coolingTempRisePrefThroughItEquipment']),
  powerDistribution: new Set(['powerBuswayLengthFt']),
} as const;

/** Descriptor ids that expose Duplicate on a child row. */
export const INSTANCE_UNIT_FIELDS_BY_SECTION: Record<
  string,
  ReadonlySet<string>
> = {
  racks: INSTANCE_UNIT_FIELDS.racks,
  'cooling-system': INSTANCE_UNIT_FIELDS.coolingSystems,
  'power-distribution': INSTANCE_UNIT_FIELDS.powerDistribution,
};

/**
 * Duplicate copies the source child's chip onto the new row, including the
 * Other side (`m` / `mm` / `C`). A new blank child still uses America.
 */
/**
 * Give a saved row the chip that was showing, without changing the field
 * default. A later row can move the field key; this row keeps its own.
 */
export function pinInstanceDimensionUnits(
  units: DimensionUnits,
  instanceId: number,
  fields: ReadonlySet<string>,
): DimensionUnits {
  if (!Number.isFinite(instanceId) || fields.size === 0) return units;
  const next: DimensionUnits = { ...units };
  let changed = false;
  for (const field of fields) {
    const key = dimensionUnitStorageKey(field, instanceId);
    if (next[key]) continue;
    const code = savedUnitCode(field, units, instanceId);
    if (!code) continue;
    next[key] = code;
    changed = true;
  }
  return changed ? next : units;
}

/**
 * Freeze the chip each listed child is showing. A later edit writes the field
 * key for the next row; children that only had that key would otherwise follow
 * it, so the first child changed whenever a duplicate's unit changed.
 */
export function pinListedInstanceDimensionUnits(
  units: DimensionUnits,
  instanceIds: readonly number[],
  fields: ReadonlySet<string>,
): DimensionUnits {
  let next = units;
  for (const instanceId of instanceIds) {
    const pinned = pinInstanceDimensionUnits(next, instanceId, fields);
    if (pinned !== next) next = pinned;
  }
  return next;
}

export function copyInstanceDimensionUnits(
  units: unknown,
  sourceId: number,
  destId: number,
  fields: ReadonlySet<string>,
): DimensionUnits {
  const parsed = parseDimensionUnits(units);
  if (
    !Number.isFinite(sourceId) ||
    !Number.isFinite(destId) ||
    sourceId === destId ||
    fields.size === 0
  ) {
    return parsed;
  }
  const next: DimensionUnits = { ...parsed };
  for (const field of fields) {
    const code = savedUnitCode(field, parsed, sourceId);
    if (!code) continue;
    next[dimensionUnitStorageKey(field, destId)] = code;
  }
  return next;
}

/**
 * Point units saved against a new row's temporary id at the id the server assigned.
 * `fields` keeps a rack temp id from rewriting a cooling unit that reused that number.
 */
export function remapInstanceUnitIds(
  units: unknown,
  idMap: ReadonlyMap<number, number>,
  fields?: ReadonlySet<string>,
): DimensionUnits {
  const parsed = parseDimensionUnits(units);
  if (idMap.size === 0) return parsed;
  const next: DimensionUnits = {};
  for (const [key, code] of Object.entries(parsed)) {
    const match = INSTANCE_UNIT_KEY.exec(key);
    const realId = match ? idMap.get(Number(match[2])) : undefined;
    if (match && realId != null && (fields == null || fields.has(match[1]))) {
      next[`${match[1]}#${realId}`] = code;
    } else {
      next[key] = code;
    }
  }
  return next;
}

/** Aggregate create idMap families that own a chip (`powerDistribution:-101`). */
const INSTANCE_UNIT_FIELDS_BY_FAMILY: Record<string, ReadonlySet<string>> = {
  racks: INSTANCE_UNIT_FIELDS.racks,
  coolingSystems: INSTANCE_UNIT_FIELDS.coolingSystems,
  powerDistribution: INSTANCE_UNIT_FIELDS.powerDistribution,
};

/**
 * Instance chips win over a seeded field-level preference. Without this, a prior
 * Other default (`m` / `C`) stays on the field key while the user picked America
 * on `#-101` — Request Detail looks up the real id, misses, and shows Other again
 * (busway ft → m, cooling F → C).
 */
export function reconcileInstanceDimensionUnitDefaults(
  units: DimensionUnits,
): DimensionUnits {
  const fromInstance = fieldLevelDimensionUnits(units);
  if (Object.keys(fromInstance).length === 0) return units;
  return { ...units, ...fromInstance };
}

/**
 * Create Request save remaps draft row ids (`applyAggregateCreateIdMap`) but
 * used to leave chips on `#-101`. Request Detail then reads the real id and
 * falls back to America (busway m → ft). Apply the same family idMap to chips,
 * then fold instance codes onto the field key so a stale preference cannot win
 * when the temp id is gone (ft/F after a seeded m/C).
 */
export function remapAggregateCreateDimensionUnits(
  units: unknown,
  idMap: Record<string, number> | undefined,
): DimensionUnits {
  const parsed = parseDimensionUnits(units);
  if (!idMap) return reconcileInstanceDimensionUnitDefaults(parsed);
  const byFamily = new Map<string, Map<number, number>>();
  for (const [clientKey, realId] of Object.entries(idMap)) {
    if (!Number.isFinite(realId)) continue;
    const splitAt = clientKey.indexOf(':');
    if (splitAt <= 0) continue;
    const tempToken = clientKey.slice(splitAt + 1);
    if (!/^-?\d+$/.test(tempToken)) continue;
    const family = clientKey.slice(0, splitAt);
    let familyMap = byFamily.get(family);
    if (!familyMap) {
      familyMap = new Map();
      byFamily.set(family, familyMap);
    }
    familyMap.set(Number(tempToken), realId);
  }
  let next = parsed;
  for (const [family, familyMap] of byFamily) {
    const fields = INSTANCE_UNIT_FIELDS_BY_FAMILY[family];
    if (!fields || familyMap.size === 0) continue;
    next = remapInstanceUnitIds(next, familyMap, fields);
  }
  return reconcileInstanceDimensionUnitDefaults(next);
}

export function parseDimensionUnits(value: unknown): DimensionUnits {
  if (value == null || value === '') return {};
  let record: unknown = value;
  if (typeof value === 'string') {
    try {
      record = JSON.parse(value);
    } catch {
      return {};
    }
  }
  if (record == null || typeof record !== 'object' || Array.isArray(record)) {
    return {};
  }
  const units: DimensionUnits = {};
  for (const [key, unit] of Object.entries(record)) {
    const match = INSTANCE_UNIT_KEY.exec(key);
    const fieldKey = match ? match[1] : key;
    if (
      typeof unit !== 'string' ||
      !UNIT_TO_SIDE[unit] ||
      !FIELD_PAIRS[fieldKey]
    ) {
      continue;
    }
    const code = unit === 'lb/sqft' ? 'lbs/sqft' : unit;
    const pair = FIELD_PAIRS[fieldKey];
    if (PAIR_CODES[pair].has(code)) {
      units[match ? `${fieldKey}#${match[2]}` : fieldKey] = code;
    }
  }
  return units;
}

export function isDimensionField(fieldKey: string): boolean {
  return Object.prototype.hasOwnProperty.call(FIELD_PAIRS, fieldKey);
}

export function sideForField(
  units: DimensionUnits | undefined,
  fieldKey: string,
  instanceId?: number,
): ChipSide {
  const pair = FIELD_PAIRS[fieldKey];
  if (!pair) return 'america';
  const scoped =
    instanceId == null
      ? undefined
      : units?.[dimensionUnitStorageKey(fieldKey, instanceId)];
  const code = scoped ?? units?.[fieldKey];
  if (!code) return 'america';
  return UNIT_TO_SIDE[code] ?? 'america';
}

export function unitCodeForSide(
  fieldKey: string,
  side: ChipSide,
): string | null {
  const pair = FIELD_PAIRS[fieldKey];
  if (!pair) return null;
  return chipLabel(pair, side);
}

/** A chip change is a saveable edit. Save only sends fields in this set. */
export function markDimensionUnitsDirty(
  dirty: ReadonlySet<string>,
): Set<string> {
  const next = new Set(dirty);
  next.add('dimensionUnits');
  return next;
}

/** Unit the chip showed for one stored key, including the America chip when nothing was saved. */
export function historyUnitCode(
  units: unknown,
  storageKey: string,
): string | undefined {
  const match = INSTANCE_UNIT_KEY.exec(storageKey);
  const fieldKey = match?.[1] ?? storageKey;
  const instanceId = match ? Number(match[2]) : undefined;
  return savedUnitCode(fieldKey, units, instanceId);
}

function savedUnitCode(
  fieldKey: string,
  units: unknown,
  instanceId?: number,
): string | undefined {
  const pair = FIELD_PAIRS[fieldKey];
  if (!pair) return undefined;
  const parsed = parseDimensionUnits(units);
  const scoped =
    instanceId == null
      ? undefined
      : parsed[dimensionUnitStorageKey(fieldKey, instanceId)];
  return scoped ?? parsed[fieldKey] ?? chipLabel(pair, 'america');
}

export function withSavedUnit(
  label: string,
  fieldKey: string,
  units: unknown,
  instanceId?: number,
): string {
  const code = savedUnitCode(fieldKey, units, instanceId);
  if (!code) return label;
  return `${stripTrailingUnitSuffixes(label)} (${code})`;
}

const TRAILING_VALUE_UNIT =
  /\s+(?:ft\/in|lbs\/sqft|lb\/sqft|kg\/m2|ft|in|mm|m|F|C)$/i;

/** View, summary, Word, and PDF keep the number bare. The unit is on the label. */
export function valueWithSavedUnit(
  value: string | number | boolean | null | undefined,
  fieldKey: string,
  units: unknown,
  instanceId?: number,
): string | number | boolean | null | undefined {
  if (!savedUnitCode(fieldKey, units, instanceId)) return value;
  if (value == null || value === '' || value === false) return value;
  const text = String(value).trim();
  if (text === '' || text === '—' || text === '-') return value;
  const bare = text.replace(TRAILING_VALUE_UNIT, '');
  return bare === text ? value : bare;
}

/**
 * First-layer list columns have one shared header, so the unit stays on the number.
 * Field labels in view, Word, and PDF keep the unit in parentheses instead.
 */
const LIST_COLUMN_UNIT_FIELDS: Record<string, Record<string, string>> = {
  'power-distribution': { LENGTH: 'powerBuswayLengthFt' },
  'cooling-system': { ΔT: 'coolingTempRisePrefThroughItEquipment' },
};

export function listCellWithSavedUnit(
  sectionId: string,
  column: string,
  value: string,
  units: unknown,
  instanceId?: number,
): string {
  const fieldKey = LIST_COLUMN_UNIT_FIELDS[sectionId]?.[column];
  if (!fieldKey) return value;
  const code = savedUnitCode(fieldKey, units, instanceId);
  if (!code) return value;
  const text = value.trim();
  if (text === '' || text === '—' || text === '-') return value;
  const bare = text.replace(TRAILING_VALUE_UNIT, '');
  if (bare === '') return value;
  return `${bare} ${code}`;
}
